// Repetidos & Venta (Req. 13.3).
//
// Cada repetido se muestra como una tarjeta EXPANDIBLE:
//   - Colapsada: carátula del maestro, artista/título, nº de catálogo, copias y
//     estado de venta.
//   - Expandida: todos los datos VIVOS del disco maestro (año, género, sello,
//     tipo, tamaño, condiciones del maestro, precios), edición de las condiciones
//     del EJEMPLAR en venta (media + carátula, que pueden diferir del maestro),
//     precio de venta + moneda + marca "a la venta", enlace a la ficha del
//     maestro, y un bloque placeholder para la futura publicación en Discogs.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AlertMessage, AsyncBoundary, Card, Icon, PageHeader } from '../ui/components.js';
import { ConditionSelect } from './vinylFields.js';
import type { ConditionGrade, DuplicateItem, Record as VinylRecord } from '../types.js';

export function DuplicatesView() {
  const { t } = useTranslation();
  const api = useApi();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('duplicates', 'read_write');
  const list = useAsync(() => api.duplicates.list(), []);
  const [adding, setAdding] = useState(false);

  async function remove(item: DuplicateItem) {
    if (!confirm(t('duplicates.removeConfirm'))) return;
    await api.duplicates.remove(item.id);
    list.reload();
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('duplicates.pageTitle')}
        subtitle={t('duplicates.pageSubtitle')}
        back
        meta={
          canEdit ? (
            <button className="btn-secondary btn-sm" type="button" onClick={() => setAdding((a) => !a)}>
              <Icon name={adding ? 'close' : 'add'} className="text-[16px]" /> {adding ? t('duplicates.close') : t('duplicates.add')}
            </button>
          ) : undefined
        }
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      {adding && canEdit && <AddForm onCreated={() => { setAdding(false); list.reload(); }} />}
      <AsyncBoundary state={list} empty={(d) => d.items.length === 0}>
        {(d) => (
          <Card title={t('duplicates.count', { count: d.total })} icon="content_copy">
            <ul className="flex flex-col gap-space-sm">
              {d.items.map((item) => (
                <DuplicateCard
                  key={item.id}
                  item={item}
                  canEdit={canEdit}
                  onChanged={() => list.reload()}
                  onRemove={() => void remove(item)}
                />
              ))}
            </ul>
          </Card>
        )}
      </AsyncBoundary>
      </div>
    </div>
  );
}

/** Tarjeta expandible de un repetido con datos del maestro y edición de venta. */
function DuplicateCard({
  item,
  canEdit,
  onChanged,
  onRemove,
}: {
  item: DuplicateItem;
  canEdit: boolean;
  onChanged: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const artist = item.master_artist_name ?? item.artist_name ?? '—';
  const title = item.master_title ?? item.title ?? '—';
  const catalog = item.master_label_catalog_nr ?? null;

  return (
    <li className="surface-rack-card !p-0 overflow-hidden">
      {/* Cabecera colapsable */}
      <button
        type="button"
        className="w-full text-left p-space-sm flex items-center gap-space-sm"
        onClick={() => setOpen((o) => !o)}
      >
        <Cover url={item.cover_image_url ?? null} />
        <div className="min-w-0 flex-1">
          <div className="font-body-md text-body-md text-on-surface truncate">
            {artist} — {title}
          </div>
          <div className="font-body-sm text-body-sm text-on-surface-variant truncate">
            {catalog ? t('duplicates.catPrefix', { catalog }) : ''}{t('duplicates.copies', { count: item.copies })}
            {item.for_sale ? t('duplicates.forSaleSuffix') : ''}
          </div>
        </div>
        {item.for_sale && (
          <span className="badge-goldmine bg-goldmine-mint/15 text-goldmine-mint border border-goldmine-mint/40 shrink-0">
            {item.sale_price != null ? `${fmtPrice(item.sale_price, item.sale_currency)}` : t('duplicates.saleBadge')}
          </span>
        )}
        <Icon name={open ? 'expand_less' : 'expand_more'} className="text-[20px] text-on-surface-variant shrink-0" />
      </button>

      {open && (
        <div className="px-space-sm pb-space-sm flex flex-col gap-space-md border-t border-border-subtle pt-space-sm">
          <MasterDetails item={item} />
          {canEdit ? (
            <SaleEditor item={item} onSaved={onChanged} />
          ) : (
            <ReadOnlySale item={item} />
          )}
          <DiscogsPublishPlaceholder item={item} />
          {/* Vínculo con el disco maestro: ver ficha si está vinculado, o
              vincular a un disco del catálogo si quedó suelto. */}
          {item.record_id ? (
            <Link to={`/records/${item.record_id}`} className="btn-secondary btn-sm self-start">
              <Icon name="album" className="text-[16px]" /> {t('duplicates.viewRecord')}
            </Link>
          ) : canEdit ? (
            <LinkMaster item={item} onLinked={onChanged} />
          ) : (
            <span className="font-body-sm text-body-sm text-on-surface-variant italic">
              {t('duplicates.noMasterLinked')}
            </span>
          )}

          {canEdit && (
            <button className="btn-ghost btn-sm !text-priority-urgent self-end" type="button" onClick={onRemove}>
              <Icon name="delete" className="text-[16px]" /> {t('duplicates.remove')}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/** Datos vivos del disco maestro (solo lectura). */
function MasterDetails({ item }: { item: DuplicateItem }) {
  const { t } = useTranslation();
  const rows: { label: string; value: string | number | null | undefined }[] = [
    { label: t('duplicates.fieldCatalogNr'), value: item.master_label_catalog_nr },
    { label: t('duplicates.fieldLabel'), value: item.master_label_name },
    { label: t('duplicates.fieldGenre'), value: item.master_genre_name ?? item.genre_label },
    { label: t('duplicates.fieldType'), value: item.master_type ?? item.type },
    { label: t('duplicates.fieldSize'), value: item.master_size_inches ? `${item.master_size_inches}"` : null },
    { label: t('duplicates.fieldEditionYear'), value: item.master_edition_year ?? item.release_year },
    { label: t('duplicates.fieldOriginalYear'), value: item.master_original_release_year },
    { label: t('duplicates.fieldOrigin'), value: item.master_origin_country },
    { label: t('duplicates.fieldColor'), value: item.master_vinyl_color },
    { label: t('duplicates.fieldMasterMedia'), value: item.master_media_condition },
    { label: t('duplicates.fieldMasterSleeve'), value: item.master_sleeve_condition },
    { label: t('duplicates.fieldCommercialValue'), value: item.master_commercial_value != null ? fmtPrice(item.master_commercial_value, 'USD') : null },
    {
      label: t('duplicates.fieldDiscogsPrice'),
      value:
        item.master_discogs_price_mid != null
          ? fmtPrice(item.master_discogs_price_mid, item.master_discogs_price_currency)
          : null,
    },
    { label: t('duplicates.fieldDiscogsRelease'), value: item.master_discogs_release_id },
  ];
  const present = rows.filter((r) => r.value != null && r.value !== '');
  if (present.length === 0) {
    return (
      <p className="font-body-sm text-body-sm text-on-surface-variant italic">
        {t('duplicates.masterNoData')}
      </p>
    );
  }
  return (
    <div>
      <p className="section-label text-on-surface-variant mb-space-xs">{t('duplicates.recordData')}</p>
      <dl className="grid grid-cols-2 gap-x-space-sm gap-y-1">
        {present.map((r) => (
          <div key={r.label} className="flex flex-col">
            <dt className="font-badge-micro text-badge-micro text-on-surface-variant uppercase">{r.label}</dt>
            <dd className="font-body-sm text-body-sm text-on-surface truncate">{String(r.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Editor de condición del ejemplar + precio de venta + marca "a la venta". */
function SaleEditor({ item, onSaved }: { item: DuplicateItem; onSaved: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [media, setMedia] = useState<string | null>(item.media_condition);
  const [sleeve, setSleeve] = useState<string | null>(item.sleeve_condition);
  const [price, setPrice] = useState<string>(item.sale_price != null ? String(item.sale_price) : '');
  const [currency, setCurrency] = useState<string>(item.sale_currency ?? 'USD');
  const [forSale, setForSale] = useState<boolean>(item.for_sale);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.duplicates.update(item.id, {
        media_condition: (media || null) as ConditionGrade | null,
        sleeve_condition: (sleeve || null) as ConditionGrade | null,
        sale_price: price.trim() === '' ? null : Number(price),
        sale_currency: currency.trim() || 'USD',
        for_sale: forSale,
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('duplicates.errSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-space-sm">
      <p className="section-label text-on-surface-variant">{t('duplicates.saleItem')}</p>
      <div className="grid grid-cols-2 gap-space-sm">
        <ConditionSelect label={t('duplicates.conditionMedia')} value={media} onChange={setMedia} />
        <ConditionSelect label={t('duplicates.conditionSleeve')} value={sleeve} onChange={setSleeve} />
      </div>
      <div className="grid grid-cols-3 gap-space-sm items-end">
        <div className="col-span-2 flex flex-col gap-space-xs">
          <label className="field-label">{t('duplicates.salePrice')}</label>
          <div className="field-slot">
            <input
              className="field-input"
              type="number"
              min={0}
              step="0.01"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-space-xs">
          <label className="field-label">{t('duplicates.currency')}</label>
          <div className="field-slot">
            <input
              className="field-input"
              value={currency}
              maxLength={8}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            />
          </div>
        </div>
      </div>
      <label className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface cursor-pointer">
        <input type="checkbox" className="accent-primary w-4 h-4" checked={forSale} onChange={(e) => setForSale(e.target.checked)} />
        {t('duplicates.markForSale')}
      </label>

      <AlertMessage message={error} />
      {saved && <p className="text-goldmine-mint font-body-sm text-body-sm">{t('duplicates.saved')}</p>}
      <button className="btn-primary w-full" type="button" disabled={busy} onClick={() => void save()}>
        <Icon name="save" className="text-[18px]" /> {busy ? t('duplicates.saving') : t('duplicates.saveItem')}
      </button>
    </div>
  );
}

/** Vista de solo lectura de los datos de venta (sin permiso de edición). */
function ReadOnlySale({ item }: { item: DuplicateItem }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <p className="section-label text-on-surface-variant">{t('duplicates.saleItem')}</p>
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        {t('duplicates.roMediaSleeve', { media: item.media_condition ?? '—', sleeve: item.sleeve_condition ?? '—' })}
      </p>
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        {t('duplicates.roPrice', { price: item.sale_price != null ? fmtPrice(item.sale_price, item.sale_currency) : '—' })}
        {item.for_sale ? t('duplicates.forSaleSuffix') : ''}
      </p>
    </div>
  );
}

/**
 * Placeholder de la publicación en Discogs (v2). Deshabilitado; deja el layout
 * listo para cuando se implemente la escritura al inventario de Discogs.
 */
function DiscogsPublishPlaceholder({ item }: { item: DuplicateItem }) {
  const { t } = useTranslation();
  const canList = item.master_discogs_release_id != null;
  return (
    <div className="rounded-lg border border-dashed border-border-subtle p-space-sm flex items-center gap-space-sm opacity-70">
      <Icon name="storefront" className="text-[20px] text-on-surface-variant" />
      <div className="min-w-0 flex-1">
        <p className="font-body-sm text-body-sm text-on-surface">{t('duplicates.publishDiscogs')}</p>
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {canList
            ? t('duplicates.publishSoonLinked')
            : t('duplicates.publishNeedsLink')}
        </p>
      </div>
      <span className="badge-goldmine bg-surface-rack text-on-surface-variant border border-border-subtle shrink-0">
        {t('duplicates.comingSoon')}
      </span>
    </div>
  );
}

function Cover({ url }: { url: string | null }) {
  if (!url) {
    return (
      <div className="w-12 h-12 shrink-0 rounded bg-surface-container flex items-center justify-center">
        <Icon name="album" className="text-[22px] text-on-surface-variant" />
      </div>
    );
  }
  return <img src={url} alt="" className="w-12 h-12 shrink-0 rounded object-cover" />;
}

function fmtPrice(value: number, currency: string | null | undefined): string {
  const cur = (currency ?? 'USD').toUpperCase();
  return `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
}

/**
 * Alta de un repetido. Vía PREFERIDA: elegir el disco del catálogo con el
 * buscador (queda vinculado por `record_id` y copia artista/título/condiciones/
 * nº catálogo del maestro). Alternativa: alta manual suelta (sin vínculo) para
 * casos sin disco en el catálogo.
 */
function AddForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [manual, setManual] = useState(false);
  const [artist, setArtist] = useState('');
  const [title, setTitle] = useState('');
  const [copies, setCopies] = useState(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function createFromRecord(rec: VinylRecord) {
    setBusy(true);
    setError(null);
    try {
      await api.duplicates.create({
        record_id: rec.id,
        artist_name: rec.artist_name ?? null,
        title: rec.title ?? null,
        release_year: rec.edition_year ?? null,
        type: rec.type ?? null,
        copies: 2,
        media_condition: rec.media_condition ?? null,
        sleeve_condition: rec.sleeve_condition ?? null,
        size_inches: rec.size_inches ?? null,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('duplicates.errCreate'));
    } finally {
      setBusy(false);
    }
  }

  async function createManual(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.duplicates.create({ artist_name: artist, title, copies });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('duplicates.errCreate'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('duplicates.addTitle')} icon="add_circle">
      {!manual ? (
        <div className="flex flex-col gap-space-sm">
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            {t('duplicates.searchHint')}
          </p>
          <RecordPicker onSelect={(rec) => void createFromRecord(rec)} disabled={busy} />
          <AlertMessage message={error} />
          <button className="btn-ghost btn-sm self-start" type="button" onClick={() => setManual(true)}>
            <Icon name="edit_note" className="text-[16px]" /> {t('duplicates.addManualToggle')}
          </button>
        </div>
      ) : (
        <form onSubmit={createManual} className="flex flex-col gap-space-sm">
          <div className="grid grid-cols-2 gap-space-sm">
            <Field label={t('duplicates.fieldArtist')}><input className="field-input" value={artist} onChange={(e) => setArtist(e.target.value)} required /></Field>
            <Field label={t('duplicates.fieldTitle')}><input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} required /></Field>
            <Field label={t('duplicates.fieldCopies')}><input className="field-input" type="number" min={1} value={copies} onChange={(e) => setCopies(Number(e.target.value))} /></Field>
          </div>
          <AlertMessage message={error} />
          <div className="flex items-center gap-space-sm">
            <button className="btn-secondary btn-sm" type="button" onClick={() => setManual(false)}>
              <Icon name="arrow_back" className="text-[16px]" /> {t('duplicates.searchInCatalog')}
            </button>
            <button className="btn-primary flex-1" type="submit" disabled={busy}>{busy ? t('duplicates.saving') : t('duplicates.save')}</button>
          </div>
        </form>
      )}
    </Card>
  );
}

/** Botón + buscador para vincular un repetido suelto a un disco del catálogo. */
function LinkMaster({ item, onLinked }: { item: DuplicateItem; onLinked: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function link(rec: VinylRecord) {
    setBusy(true);
    setError(null);
    try {
      await api.duplicates.update(item.id, { record_id: rec.id });
      setOpen(false);
      onLinked();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('duplicates.errLink'));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="btn-secondary btn-sm self-start" type="button" onClick={() => setOpen(true)}>
        <Icon name="link" className="text-[16px]" /> {t('duplicates.linkToRecord')}
      </button>
    );
  }
  return (
    <div className="flex flex-col gap-space-xs">
      <RecordPicker onSelect={(rec) => void link(rec)} disabled={busy} />
      <AlertMessage message={error} />
      <button className="btn-ghost btn-sm self-start" type="button" onClick={() => setOpen(false)}>
        {t('duplicates.cancel')}
      </button>
    </div>
  );
}

/**
 * Autocompletar de discos del catálogo por texto (artista/título/nº catálogo),
 * con debounce. Al elegir uno, invoca `onSelect(record)`.
 */
function RecordPicker({
  onSelect,
  disabled,
}: {
  onSelect: (record: VinylRecord) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<VinylRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.records.quickSearch(term, { page: 1, page_size: 8 });
        if (cancelled) return;
        setResults(res.items);
        setOpen(res.items.length > 0);
      } catch {
        if (!cancelled) {
          setResults([]);
          setOpen(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, q]);

  return (
    <div className="relative" ref={boxRef}>
      <div className="field-slot">
        <Icon name="search" className="text-on-surface-variant text-[16px] mr-space-xs" />
        <input
          className="field-input"
          placeholder={t('duplicates.pickerPlaceholder')}
          value={q}
          disabled={disabled}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          autoComplete="off"
        />
        {loading && <Icon name="progress_activity" className="text-on-surface-variant text-[16px] animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-20 top-full left-0 right-0 mt-1 max-h-64 overflow-auto rounded-lg bg-surface-container-high border border-border-subtle shadow-lg">
          {results.map((rec) => (
            <li key={rec.id}>
              <button
                type="button"
                className="w-full text-left px-space-sm py-2 flex items-center gap-space-sm hover:bg-surface-rack"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(rec);
                  setQ('');
                  setOpen(false);
                }}
              >
                <Cover url={rec.cover_image_url ?? null} />
                <span className="min-w-0 flex-1">
                  <span className="block font-body-sm text-body-sm text-on-surface truncate">
                    {rec.artist_name ?? '—'} — {rec.title}
                  </span>
                  <span className="block font-body-sm text-body-sm text-on-surface-variant truncate">
                    {rec.label_catalog_nr ? t('duplicates.catPrefix', { catalog: rec.label_catalog_nr }) : ''}
                    {rec.edition_year ?? ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-space-xs">
      <label className="field-label">{label}</label>
      <div className="field-slot">{children}</div>
    </div>
  );
}
